import { useState } from "react";
import { generateAvatar, getAvatarPrompt } from "../services/guides";
import type { CharacterProfile } from "../types/guide";

interface GuideAvatarPickerProps {
  profile: CharacterProfile;
  savedId: string | null;
  onUpdateProfile: (updated: CharacterProfile | Partial<CharacterProfile>) => void;
  setStatusMsg: (msg: string, isError?: boolean) => void;
}

export function GuideAvatarPicker({
  profile,
  savedId,
  onUpdateProfile,
  setStatusMsg,
}: GuideAvatarPickerProps) {
  const [generating, setGenerating] = useState(false);
  const [showUrlField, setShowUrlField] = useState(false);
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");

  // The backend's style-sheet prompt for this guide, fetched when the modal
  // opens. The textarea starts from it; `customPrompt` is only sent when the
  // admin changed the text, so the backend template stays the single source.
  const [defaultPrompt, setDefaultPrompt] = useState("");
  const [loadingPrompt, setLoadingPrompt] = useState(false);

  const handleOpenModal = async () => {
    if (!savedId) {
      setStatusMsg("Save companion first to generate AI avatar.", true);
      return;
    }
    setLoadingPrompt(true);
    try {
      const { prompt } = await getAvatarPrompt(savedId);
      setDefaultPrompt(prompt);
      setCustomPrompt(prompt);
      setShowPromptModal(true);
    } catch (e) {
      setStatusMsg(`Could not load the avatar prompt: ${e instanceof Error ? e.message : e}`, true);
    } finally {
      setLoadingPrompt(false);
    }
  };

  const handleConfirmGenerate = async () => {
    if (!savedId) return;

    const edited = customPrompt.trim() !== defaultPrompt.trim();
    setShowPromptModal(false);
    setGenerating(true);
    try {
      const updated = await generateAvatar(savedId, edited ? customPrompt.trim() : undefined);
      onUpdateProfile(updated);
      setStatusMsg("Avatar generated and saved successfully!");
    } catch (e) {
      setStatusMsg(`Avatar generation failed: ${e instanceof Error ? e.message : e}`, true);
    } finally {
      setGenerating(false);
    }
  };

  const accentColor = profile.accentColor || "var(--accent, #4d8bfa)";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      <label style={{ margin: 0 }}>Guide Avatar</label>

      {/* Avatar Image Frame */}
      <div
        style={{
          width: "120px",
          height: "120px",
          borderRadius: "var(--radius, 9px)",
          overflow: "hidden",
          backgroundColor: "var(--panel-2)",
          border: `2px solid ${accentColor}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        {profile.imageUrl ? (
          <img
            src={profile.imageUrl}
            alt={profile.name || "Guide avatar"}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
        ) : (
          <span style={{ fontSize: "2.5rem", opacity: 0.6 }}>👤</span>
        )}

        {generating && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontSize: "12px",
            }}
          >
            Generating...
          </div>
        )}
      </div>

      {/* Action Button: Opens Prompt Modal */}
      <button
        className="btn"
        type="button"
        onClick={handleOpenModal}
        disabled={generating || loadingPrompt || !savedId}
        style={{ width: "120px", padding: "6px 10px", fontSize: "12px" }}
      >
        {generating
          ? "…Generating"
          : loadingPrompt
          ? "…Loading"
          : profile.imageUrl
          ? "🔄 Regenerate"
          : "🎨 Generate Avatar"}
      </button>

      {!savedId && (
        <span className="muted" style={{ fontSize: "11px", maxWidth: "120px" }}>
          Save companion first to generate.
        </span>
      )}

      {/* Collapsible Manual URL Edit */}
      <button
        className="btn btn-ghost muted"
        type="button"
        style={{
          fontSize: "11px",
          textAlign: "left",
          padding: 0,
          border: "none",
          width: "fit-content",
        }}
        onClick={() => setShowUrlField(!showUrlField)}
      >
        {showUrlField ? "▾ Hide URL" : "▸ Edit Image URL"}
      </button>

      {showUrlField && (
        <div style={{ width: "100%", maxWidth: "180px" }}>
          <input
            type="text"
            value={profile.imageUrl}
            placeholder="https://..."
            onChange={(e) => onUpdateProfile({ imageUrl: e.target.value })}
            style={{ fontSize: "12px", padding: "5px 8px" }}
          />
        </div>
      )}

      {/* Prompt Review & Edit Modal */}
      {showPromptModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            backdropFilter: "blur(4px)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem",
          }}
          onClick={() => setShowPromptModal(false)}
        >
          <div
            style={{
              backgroundColor: "var(--panel, #1e1e2d)",
              border: "1px solid var(--border, rgba(255, 255, 255, 0.12))",
              borderRadius: "14px",
              padding: "1.25rem 1.5rem",
              maxWidth: "560px",
              width: "100%",
              boxShadow: "0 12px 32px rgba(0, 0, 0, 0.5)",
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 600 }}>🎨 Image Generation Prompt</h3>
              <button
                className="btn btn-ghost"
                style={{ padding: "4px 8px", fontSize: "1.1rem", cursor: "pointer" }}
                onClick={() => setShowPromptModal(false)}
              >
                ✕
              </button>
            </div>

            <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--muted, #97a3b8)" }}>
              This is the Aura avatar style sheet filled in for this guide (soft 3D marshmallow-style human on the sky-blue gradient). Generate as is to match the shipped cast, or edit the text for a one-off.
            </p>

            <textarea
              rows={6}
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              placeholder="Enter prompt description..."
              style={{
                width: "100%",
                padding: "10px 12px",
                fontSize: "0.85rem",
                borderRadius: "8px",
                backgroundColor: "var(--panel-2, #141b2d)",
                border: "1px solid var(--border, rgba(255, 255, 255, 0.15))",
                color: "var(--text, #e8ecf4)",
                fontFamily: "inherit",
                lineHeight: "1.4",
                resize: "vertical",
              }}
            />

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "0.25rem" }}>
              <button
                className="btn btn-ghost"
                type="button"
                style={{ fontSize: "0.8rem", color: "var(--muted, #97a3b8)" }}
                onClick={() => setCustomPrompt(defaultPrompt)}
                disabled={customPrompt === defaultPrompt}
              >
                🔄 Reset to style sheet
              </button>

              <div style={{ display: "flex", gap: "0.5rem" }}>
                <button
                  className="btn btn-ghost"
                  type="button"
                  onClick={() => setShowPromptModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={handleConfirmGenerate}
                  style={{ fontWeight: 600 }}
                >
                  ✨ Generate Avatar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
